import { apiClient, ApiResponse } from './client';

export interface Subscription {
    id: string;
    userId: string;
    variantId: string;
    priceId: string;
    status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'unpaid' | 'paused';
    currentPeriodStart?: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    providerId?: string;
    createdAt: string;
    updatedAt: string;
    metadata?: string;
    variant: {
        id: string;
        country: string;
        currency: string;
        product: {
            id: string;
            name: string;
            description?: string;
        };
    };
    price: {
        id: string;
        amount: number;
        currency: string;
        interval: string;
    };
}

export interface PlanChangePreview {
    changeType: 'UPGRADE' | 'DOWNGRADE' | 'BILLING_UPGRADE' | 'BILLING_DOWNGRADE' | 'SAME';
    currentPlan: {
        name: string;
        price: number;
        currency: string;
        interval: string;
        billingInterval: string;
    };
    newPlan: {
        name: string;
        price: number;
        currency: string;
        interval: string;
        billingInterval: string;
    };
    effectiveDate: string | null;
    nextPaymentDate: string | null;
    prorationAmount: number | null;
    unusedTimeAmount?: number | null;
    totalAmountDue?: number | null;
    message: string;
}

export interface PlanChangeResult extends PlanChangePreview {
    success: boolean;
    subscription?: Subscription;
}

export interface BillingInvoice {
    id: string;
    date: string;
    amount: number;
    rawAmount: number;
    currency: string;
    status: string;
    invoicePdf?: string;
    hostedInvoiceUrl?: string;
    last4?: string;
    description?: string;
}

export interface PaymentMethod {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    isDefault: boolean;
}

export interface BillingStatus {
    billingSource: 'stripe' | 'shopify' | 'none' | null;
    billingStatus: 'active' | 'grace_period' | 'read_only' | 'suspended' | null;
    gracePeriodEndsAt: string | null;
    readOnlyEndsAt: string | null;
    suspendedAt: string | null;
    shopifyBillingShop: string | null;
    shopifySubscriptionId: string | null;
    /** Whether the Shopify app is currently installed for the billing shop.
     *  Drives the grace-banner CTA (reinstall vs website-only). */
    appInstalled?: boolean;
}

/**
 * True when a subscription is billed through Shopify (not Stripe). Shopify
 * providerIds are AppSubscription GIDs or synthetic free-tier ids. The
 * Stripe-oriented billing UI (PlanPage, ProfileModal) must ignore these: a
 * lapsed Shopify sub is downgraded to `past_due`, which would otherwise be
 * misread as a failed Stripe payment ("update your payment method").
 */
export function isShopifySubscription(sub?: { providerId?: string | null } | null): boolean {
    const id = sub?.providerId || '';
    return id.startsWith('gid://shopify/AppSubscription/') || id.startsWith('shopify-free:');
}

export const subscriptionsService = {
    getMySubscriptions: async (): Promise<ApiResponse<Subscription[]>> => {
        const response = await apiClient.get<ApiResponse<Subscription[]>>('/subscriptions');
        return response.data;
    },

    getBillingStatus: async (): Promise<ApiResponse<BillingStatus>> => {
        const response = await apiClient.get<ApiResponse<BillingStatus>>('/subscriptions/billing-status');
        return response.data;
    },

    getInvoices: async (): Promise<ApiResponse<BillingInvoice[]>> => {
        const response = await apiClient.get<ApiResponse<BillingInvoice[]>>('/subscriptions/invoices');
        return response.data;
    },

    getPaymentMethods: async (): Promise<ApiResponse<PaymentMethod[]>> => {
        const response = await apiClient.get<ApiResponse<PaymentMethod[]>>('/subscriptions/payment-methods');
        return response.data;
    },

    createSetupSession: async (): Promise<{ url: string }> => {
        const response = await apiClient.post<{ success: boolean; url: string }>('/subscriptions/payment-methods/setup', {});
        return response.data;
    },

    deletePaymentMethod: async (id: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(`/subscriptions/payment-methods/${id}`);
        return response.data;
    },

    createCheckoutSession: async (priceId: string, returnUrl?: string): Promise<{ url: string }> => {
        const response = await apiClient.post<{ success: boolean; url: string }>('/subscriptions/checkout', { priceId, returnUrl });
        return response.data;
    },

    createPortalSession: async (): Promise<{ url: string }> => {
        const response = await apiClient.post<{ success: boolean; url: string }>('/subscriptions/portal', {});
        return response.data;
    },

    syncSession: async (sessionId: string): Promise<ApiResponse<Subscription>> => {
        const response = await apiClient.post<ApiResponse<Subscription>>('/subscriptions/sync-session', { sessionId });
        return response.data;
    },

    syncBilling: async (): Promise<{ success: boolean; synced: boolean }> => {
        const response = await apiClient.post<{ success: boolean; synced: boolean }>('/subscriptions/sync-billing', {});
        return response.data;
    },

    cancelSubscription: async (): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post<{ success: boolean; message: string }>('/subscriptions/cancel', {});
        return response.data;
    },

    cancelPlanChangeSchedule: async (): Promise<ApiResponse<Subscription>> => {
        const response = await apiClient.post<ApiResponse<Subscription>>('/subscriptions/cancel-schedule', {});
        return response.data;
    },

    resumeSubscription: async (): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post<{ success: boolean; message: string }>('/subscriptions/resume', {});
        return response.data;
    },

    getCustomer: async (): Promise<ApiResponse<any>> => {
        const response = await apiClient.get<ApiResponse<any>>('/subscriptions/customer');
        return response.data;
    },

    updateCustomer: async (data: any): Promise<ApiResponse<any>> => {
        const response = await apiClient.put<ApiResponse<any>>('/subscriptions/customer', data);
        return response.data;
    },

    getChangePlanPreview: async (priceId: string): Promise<ApiResponse<PlanChangePreview>> => {
        const response = await apiClient.get<ApiResponse<PlanChangePreview>>(`/subscriptions/change-preview/${priceId}`);
        return response.data;
    },

    changePlan: async (priceId: string, immediate?: boolean): Promise<ApiResponse<PlanChangeResult>> => {
        const response = await apiClient.put<ApiResponse<PlanChangeResult>>('/subscriptions/plan', { priceId, immediate });
        return response.data;
    },

    downgradeToFree: async (): Promise<ApiResponse<{ message: string; effectiveDate: string }>> => {
        const response = await apiClient.post<ApiResponse<any>>('/subscriptions/downgrade-to-free', {});
        return response.data;
    }
};
